interface StageLike {
  id: string;
  transitions?: { to: string; when: string }[];
}

export interface ScenarioGraphProps {
  entryStage?: string;
  stages: StageLike[];
}

const TERMINALS = ['@goal', '@handoff', '@close'];
const NODE_W = 140;
const NODE_H = 40;
const GAP_X = 60;
const GAP_Y = 80;

/** Simple SVG graph: stages laid out left-to-right, terminals in a trailing row. Avoids a mermaid dependency. */
export function ScenarioGraph({ entryStage, stages }: ScenarioGraphProps) {
  if (stages.length === 0) {
    return <div className="p-4 text-sm text-muted-fg">Нет этапов для отображения</div>;
  }

  const ids = stages.map((s) => s.id);
  const usedTerminals = TERMINALS.filter((t) => stages.some((s) => (s.transitions ?? []).some((tr) => tr.to === t)));
  const allNodes = [...ids, ...usedTerminals];
  const positions = new Map<string, { x: number; y: number }>();
  allNodes.forEach((id, i) => {
    positions.set(id, { x: 20 + i * (NODE_W + GAP_X), y: 20 });
  });

  const width = 20 + allNodes.length * (NODE_W + GAP_X);
  const height = 20 + NODE_H + GAP_Y;

  const edges: { from: string; to: string; label: string }[] = [];
  for (const s of stages) {
    for (const t of s.transitions ?? []) {
      edges.push({ from: s.id, to: t.to, label: t.when });
    }
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full" role="img" aria-label="Граф сценария">
      <defs>
        <marker id="arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 z" fill="currentColor" className="text-muted-fg" />
        </marker>
      </defs>
      {edges.map((e, i) => {
        const from = positions.get(e.from);
        const to = positions.get(e.to);
        if (!from || !to) return null;
        const x1 = from.x + NODE_W;
        const y1 = from.y + NODE_H / 2;
        const x2 = to.x;
        const y2 = to.y + NODE_H / 2;
        return (
          <g key={i}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" className="text-muted-fg" markerEnd="url(#arrow)" />
            <text x={(x1 + x2) / 2} y={y1 - 6} fontSize="9" textAnchor="middle" className="fill-muted-fg">
              {e.label.slice(0, 24)}
            </text>
          </g>
        );
      })}
      {allNodes.map((id) => {
        const p = positions.get(id);
        if (!p) return null;
        const isTerminal = TERMINALS.includes(id);
        const isEntry = id === entryStage;
        return (
          <g key={id}>
            <rect
              x={p.x}
              y={p.y}
              width={NODE_W}
              height={NODE_H}
              rx={6}
              className={
                isTerminal
                  ? 'fill-muted stroke-border'
                  : isEntry
                    ? 'fill-primary/20 stroke-primary'
                    : 'fill-card stroke-border'
              }
              strokeWidth={1.5}
            />
            <text x={p.x + NODE_W / 2} y={p.y + NODE_H / 2 + 4} fontSize="11" textAnchor="middle" className="fill-fg">
              {id}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
